import React from "react";

import moment from "moment";
import { useIntl } from "react-intl";

import {
  notificationByTitleSelector,
  useNotification,
  selectAllowConfigureResourceNotifications,
  updateAllowConfigureResourceNotifications
} from "Containers/Notifications";
import { SUCCESS_TIMEOUT } from "Containers/Notifications/Notifications.utils";
import getUrl from "Libs/getUrl";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import type { Activity } from "@packages/client";

const ADD_DURATION = `${SUCCESS_TIMEOUT / 1000}`;

const isOld = (time: string) =>
  moment(moment()).isAfter(moment(time).add(ADD_DURATION, "seconds").format());

type UseActivityNotificationProps = {
  activity: Activity;
  organizationId: string;
  projectId: string;
};
export const useConfigureResourcesNotification = ({
  activity,
  organizationId,
  projectId
}: UseActivityNotificationProps) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const notify = useNotification();

  const inProgressNotificationTitle = intl.formatMessage({
    id: "configure_resources.notification.deploying.title"
  });
  const successNotificationTitle = intl.formatMessage({
    id: "configure_resources.notification.success.title"
  });
  const successNotificationTitleNonGuaranteed = intl.formatMessage({
    id: "configure_resources.page.notification.title"
  });
  const failureNotificationTitle = intl.formatMessage({
    id: "configure_resources.notification.fail.title"
  });

  const allowNotification = useAppSelector(
    selectAllowConfigureResourceNotifications
  );

  const inProgressNotification = useAppSelector(state =>
    notificationByTitleSelector(state, { title: inProgressNotificationTitle })
  );

  const successNotification = useAppSelector(state =>
    notificationByTitleSelector(state, { title: successNotificationTitle })
  );

  const successNotificationNonGuaranteed = useAppSelector(state =>
    notificationByTitleSelector(state, {
      title: successNotificationTitleNonGuaranteed
    })
  );

  const failureNotification = useAppSelector(state =>
    notificationByTitleSelector(state, { title: failureNotificationTitle })
  );

  const environmentName = activity.parameters?.environment as string;
  const isGuaranteedResources =
    !!activity?.payload?.has_guaranteed_resource_changes;

  if (
    allowNotification.inprogress &&
    activity.state === "in_progress" &&
    activity.timings?.build
  ) {
    if (
      isOld(activity.started_at ?? activity.created_at) ||
      inProgressNotification
    )
      return;

    const environmentOverviewUrl = getUrl({
      key: "organization.project.environment",
      props: {
        organizationId,
        projectId,
        environmentId: environmentName
      }
    });

    // GUARANTEED RESOURCE (IN PROGRESS)
    if (isGuaranteedResources) {
      dispatch(
        updateAllowConfigureResourceNotifications(["inprogress", false])
      );

      notify.loading({
        title: inProgressNotificationTitle,
        description: intl.formatMessage(
          {
            id: "configure_resources.notification.deploying.description"
          },
          {
            strong: content => <strong>{content}</strong>,
            environmentName
          }
        ),
        link: {
          title: intl.formatMessage(
            {
              id: "configure_resources.notification.deploying.linkTitle"
            },
            {
              environmentName
            }
          ),
          location: {
            to: environmentOverviewUrl
          },
          closeOnClick: true
        },
        timeout: SUCCESS_TIMEOUT
      });
    } else {
      // NON-GUARANTEED RESOURCE (SUCCESS)
      // If redux state already has the notification, don't show duplicate notifications
      if (successNotificationNonGuaranteed) return;

      dispatch(
        updateAllowConfigureResourceNotifications(["inprogress", false])
      );

      notify.success({
        title: successNotificationTitleNonGuaranteed,
        description: intl.formatMessage(
          {
            id: "configure_resources.page.notification.description"
          },
          {
            strong: content => <strong>{content}</strong>,
            environmentName
          }
        ),
        link: {
          title: intl.formatMessage(
            {
              id: "configure_resources.page.notification.linkTitle"
            },
            {
              environmentName
            }
          ),
          location: {
            to: environmentOverviewUrl
          },
          closeOnClick: true
        },
        timeout: SUCCESS_TIMEOUT
      });
    }
  } else if (
    // GUARANTEED RESOURCE (SUCCESS)
    allowNotification.success &&
    activity.state === "complete" &&
    activity.result === "success" &&
    isGuaranteedResources
  ) {
    if (isOld(activity.completed_at) || successNotification) return;

    dispatch(updateAllowConfigureResourceNotifications(["success", false]));

    notify.success({
      title: successNotificationTitle,
      description: intl.formatMessage(
        {
          id: "configure_resources.notification.success.description"
        },
        {
          environmentName: <strong>{environmentName}</strong>
        }
      ),
      link: {
        title: intl.formatMessage({
          id: "configure_resources.notification.success.linkTitle"
        }),
        location: {
          to: getUrl({
            key: "organization.project.environment.log",
            props: {
              organizationId,
              projectId,
              environmentId: environmentName,
              activityId: activity.id
            }
          })
        },
        closeOnClick: true
      },
      timeout: SUCCESS_TIMEOUT
    });
  } else if (
    // (FAILURE)
    allowNotification.failure &&
    activity.state === "complete" &&
    activity.result === "failure" &&
    activity?.expires_at
  ) {
    if (isOld(activity.completed_at) || failureNotification) return;

    dispatch(updateAllowConfigureResourceNotifications(["failure", false]));

    const environmentActivityLogUrl = getUrl({
      key: "organization.project.environment.log",
      props: {
        organizationId,
        projectId,
        environmentId: environmentName,
        activityId: activity.id
      }
    });

    notify.error({
      title: failureNotificationTitle,
      description: intl.formatMessage(
        {
          id: "configure_resources.notification.fail.description"
        },
        {
          environmentName: <strong>{environmentName}</strong>
        }
      ),
      link: {
        title: intl.formatMessage({
          id: "configure_resources.notification.fail.linkTitle"
        }),
        location: {
          to: environmentActivityLogUrl
        },
        closeOnClick: true
      }
    });
  }
};
